import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Grid,
  Button,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  TextField,
  InputAdornment,
} from '@mui/material';

import { useApiGet } from '../../utils/httpClient';
import { getUserList } from '../../endpoints/users';
import { useCallback } from 'react';
import TablePeo from '../../Components/TablePeo';
import SPDSelector from '../../Components/SPDSelector';
import { Add } from '@mui/icons-material';
import { roles } from '../../utils/variables';

const headCells = [
  {
    id: 'first_name',
    firstCell: true,
    label: 'First Name',
    width: '25%',
  },
  {
    id: 'last_name',
    label: 'Last Name',
    firstCell: true,
    width: '25%',
  },
  {
    id: 'user_type',
    label: 'Type',
    firstCell: true,
    width: '25%',
  },
  {
    id: 'last_chapter',
    label: 'Chapter',
    width: '25%',
  },
];

export default function UserList() {
  let [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key, value) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    searchParams.get('search') || ''
  );
  const [userType, setUserType] = useState(
    searchParams.get('type') || 'member-all'
  );
  const [spd, setSpd] = useState(searchParams.get('spd') || '');

  // need up update search params when search changes
  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  // need up update params when status changes
  useEffect(() => {
    updateSearchParams('type', userType);
  }, [userType, updateSearchParams]);

  // need up update params when spd changes
  useEffect(() => {
    updateSearchParams('spd', spd);
  }, [spd, updateSearchParams]);

  const { data, isFetching } = useApiGet(
    ['users', rowsPerPage, page, orderBy, order, search, userType, spd],
    getUserList,
    {
      enabled: true,
      retry: 1,
    },
    {
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      userType,
      spd,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handleClick = (event, id) => {
    window.location.href = `/user/${id}`;
  };

  const canFilter = () => {
    let role = localStorage.getItem('role');
    let rolesAccepted = [
      roles.Admin,
      roles.Support,
      roles.Communications,
      roles.Executive,
      roles.SPDOfficer,
      roles.Finance,
      roles.RMR,
      roles.LCOfficer,
      roles.Project,
    ];
    return rolesAccepted.includes(role);
  };

  const canAdd = () => {
    let role = localStorage.getItem('role');
    let rolesAccepted = [
      roles.Admin,
      roles.Support,
      roles.SPDOfficer,
      roles.LCOfficer,
      roles.Project,
    ];
    return rolesAccepted.includes(role);
  };

  return (
    <Grid container>
      {canAdd() && (
        <Grid item xs={12} padding textAlign={'right'}>
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = '/user/create';
            }}
            size="small"
            color="green"
          >
            <Add style={{ marginRight: 8, fontSize: 16 }} />
            Add User
          </Button>
        </Grid>
      )}
      <Grid item xs={12} md={6} padding>
        <TextField
          label="Search"
          id="search-field"
          size="small"
          value={delaySearch}
          fullWidth={true}
          onChange={(event) => setDelaySearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {canFilter() && (
        <Grid item xs={12} md={3} padding>
          <FormControl fullWidth size={'small'}>
            <InputLabel id="user-type-label">User Type</InputLabel>
            <Select
              labelId="user-type-label"
              id="user-type-select"
              value={userType}
              label="User Type"
              onChange={(event) => setUserType(event.target.value)}
            >
              <MenuItem value={'all'}>All</MenuItem>
              <MenuItem value={'member-all'}>All Members</MenuItem>
              <MenuItem value={'member-active'}>Active Members</MenuItem>
              <MenuItem value={'member-inactive'}>Inactive Members</MenuItem>
              <MenuItem value={'non-member'}>Non Member</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} md={3} padding>
        <SPDSelector
          spd={spd}
          handleSpdSelect={setSpd}
          reportId={'user-list'}
          all={true}
        />
      </Grid>
      <Grid item xs={12} padding>
        <TablePeo
          data={data}
          isFetching={isFetching}
          headCells={headCells}
          handleRowClick={handleClick}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
        />
      </Grid>
    </Grid>
  );
}
